import { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, FormField, TextInput, AudioPlayer, LogoMark } from '@/components/ui';

/**
 * פורטל ההקלטות ללקוח: טלפון → קוד בוואטסאפ → ההקלטות שלו.
 *
 * ⚠️ axios נפרד ולא apiClient: ה-interceptor שם מוחק את סשן הצוות על כל
 * 401, כך שפורטל שפג היה מנתק אדמין שפתוח בטאב אחר. הסשן ב-sessionStorage
 * (לא localStorage) כדי שימות עם הטאב ולא יתנגש בטוקן האמיתי.
 *
 * שגיאות מוצגות ב-role="alert" ולא ב-toast — ToastHost מותקן רק בשלושת
 * לייאאוטי הצוות ולא קיים בדף ציבורי.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/v1/merkaziya',
  timeout: 30_000,
});

interface Item {
  id: number;
  started_at: string | null;
  direction: string;
  duration: number;
  stream_url: string;
  download_url: string | null;
}

type Step = 'phone' | 'code' | 'list';

export function RecordingsPortalPage() {
  const { tenantSlug = '' } = useParams();
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [masked, setMasked] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const msg = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

  async function requestCode() {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/portal/${tenantSlug}/request-code`, { phone });
      setChallenge(r.data.data.challenge);
      setMasked(r.data.data.phone_masked);
      setStep('code');
    } catch (e) {
      setError(msg(e, 'שליחת הקוד נכשלה. נסה שוב.'));
    } finally { setBusy(false); }
  }

  async function verify() {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/portal/${tenantSlug}/verify`, { challenge, phone, code });
      const session = r.data.data.session;
      sessionStorage.setItem('portal_session', session);
      const list = await api.get(`/portal/${tenantSlug}/recordings`, {
        headers: { Authorization: `Portal ${session}` },
      });
      setItems(list.data.data ?? []);
      setStep('list');
    } catch (e) {
      setError(msg(e, 'הקוד שגוי או פג תוקף.'));
    } finally { setBusy(false); }
  }

  /**
   * הקישור חתום ל-15 דקות אבל הדף עשוי להישאר פתוח יותר. בכישלון נגינה
   * מרעננים את הרשימה כדי לקבל קישורים טריים.
   */
  async function refresh() {
    const session = sessionStorage.getItem('portal_session');
    if (!session) return;
    try {
      const list = await api.get(`/portal/${tenantSlug}/recordings`, {
        headers: { Authorization: `Portal ${session}` },
      });
      setItems(list.data.data ?? []);
    } catch {
      setStep('phone');
      setError('החיבור פג. אמת שוב.');
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)', padding: 'var(--gap)' }}>
      <Card padded={false} style={{ width: '100%', maxWidth: step === 'list' ? 640 : 420, padding: 'calc(var(--pad-card) * 1.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'var(--gap)' }}>
          <LogoMark size={32} />
          <div>
            <div style={{ fontSize: 'var(--t-xl)', fontWeight: 700 }}>ההקלטות שלי</div>
            <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
              {step === 'list' ? `${items.length} הקלטות` : 'אימות בוואטסאפ'}
            </div>
          </div>
        </div>

        {error && (
          <div role="alert" style={{ background: 'var(--danger-soft)', color: 'var(--danger)', padding: '10px 12px', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)', marginBottom: 'var(--gap)' }}>
            {error}
          </div>
        )}

        {step === 'phone' && (
          <form className="stack" onSubmit={(e) => { e.preventDefault(); void requestCode(); }}>
            <FormField label="מספר הטלפון שלך" hint="נשלח קוד אימות בוואטסאפ">
              <TextInput value={phone} onChange={(e) => setPhone(e.target.value)}
                inputMode="tel" dir="ltr" placeholder="050-000-0000" autoFocus />
            </FormField>
            <button type="submit" className="btn primary" disabled={busy || !phone}>
              {busy ? 'שולח…' : 'שלח לי קוד'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form className="stack" onSubmit={(e) => { e.preventDefault(); void verify(); }}>
            <FormField label="הקוד שקיבלת" hint={`נשלח ל-${masked}`}>
              {/* one-time-code מאפשר מילוי אוטומטי מההתראה בנייד. */}
              <TextInput value={code} onChange={(e) => setCode(e.target.value)}
                inputMode="numeric" maxLength={6} dir="ltr" autoComplete="one-time-code"
                placeholder="______" autoFocus />
            </FormField>
            <button type="submit" className="btn primary" disabled={busy || code.length < 6}>
              {busy ? 'בודק…' : 'אישור'}
            </button>
            <button type="button" className="btn ghost" onClick={() => { setStep('phone'); setCode(''); setError(null); }}>
              החלף מספר
            </button>
          </form>
        )}

        {step === 'list' && (
          <div className="stack" style={{ gap: 10 }}>
            <button type="button" className="btn ghost" onClick={() => void refresh()}
              style={{ fontSize: 'var(--t-xs)', alignSelf: 'flex-start' }}>
              ↻ רענן קישורים
            </button>
            {items.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text-2)', padding: '24px 0', fontSize: 'var(--t-sm)' }}>
                לא נמצאו הקלטות למספר הזה.
              </div>
            )}
            {items.map((r) => (
              <div key={r.id} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 'var(--pad-card)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 'var(--t-sm)' }}>
                  <span>{r.direction === 'inbound' ? '📞 שיחה נכנסת' : '📱 שיחה יוצאת'}</span>
                  <span style={{ color: 'var(--text-2)' }}>
                    {r.started_at ? new Date(r.started_at).toLocaleString('he-IL') : ''}
                  </span>
                </div>
                {/* בלי title — עם title הנגן מעביר ל-PersistentAudioBar שאינו מותקן בדף ציבורי. */}
                <AudioPlayer src={r.stream_url} duration={r.duration} />
                {r.download_url && (
                  <a href={r.download_url} download className="btn ghost" style={{ marginTop: 6, fontSize: 'var(--t-xs)' }}>
                    ⬇ הורד
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
